import { resolveTrionfiCardImage } from "@/lib/trionfi";
import { supabase } from "@/lib/supabaseClient";

const NEW_FOR_DAYS = 14;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const CREATION_ASSET_BUCKET = "creations-public";

const GENERATED_ARTWORK_BY_FAMILY = Object.freeze({
  hope_blanket: {
    preferredEngine: "threejs",
    placeholder: "/assets/images/hope-blanket-placeholder.jpg",
  },
  labyrinth_scarf: {
    preferredEngine: "p5",
    placeholder: "/assets/images/labyrinth-scarf-placeholder.jpg",
  },
});

const DEFAULT_CREATION_PLACEHOLDER =
  "/assets/images/creation-placeholder.jpg";
const TRIONFI_PLACEHOLDER = "/trionfi-assets/cards/trionfi_back.png";

function normaliseFamilyType(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function getPublicAssetUrl(bucket, path) {
  if (!bucket || !path) return "";

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl ?? "";
}

function getGeneratedArtwork(creationId, familyType, creationAssets = []) {
  const config = GENERATED_ARTWORK_BY_FAMILY[normaliseFamilyType(familyType)];
  if (!creationId || !config) return null;

  const finalAssets = creationAssets.filter((asset) => asset.type === "final");
  const storedAsset =
    finalAssets.find((asset) => asset.engine === config.preferredEngine) ||
    finalAssets[0] ||
    null;

  /*
   * Imported creations can keep artwork in a legacy folder whose UUID differs
   * from the current creations.id. The creation_assets row is authoritative.
   */
  const bucket = storedAsset?.bucket || CREATION_ASSET_BUCKET;
  const path =
    storedAsset?.path ||
    `creations/${creationId}/final_${config.preferredEngine}.png`;

  return {
    image: getPublicAssetUrl(bucket, path),
    fallbackImage: config.placeholder,
  };
}

export function handleCollectionImageError(event, fallbackImage) {
  const image = event.currentTarget;

  if (fallbackImage && image.dataset.fallbackAttempted !== "true") {
    image.dataset.fallbackAttempted = "true";
    image.src = fallbackImage;
    return;
  }

  // If the supplied placeholder is not present yet, leave the designed
  // background visible instead of showing the browser's broken-image icon.
  image.style.opacity = "0";
}

export function formatShortDate(iso) {
  if (!iso) return "";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isRecentlyUnlocked(value) {
  if (!value) return false;

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return false;

  const age = Date.now() - timestamp;
  return age >= 0 && age <= NEW_FOR_DAYS * DAY_IN_MS;
}

function isMissingRpc(error, functionName) {
  const message = String(error?.message ?? "").toLowerCase();
  const normalisedFunctionName = String(functionName ?? "").toLowerCase();

  return (
    error?.code === "PGRST202" ||
    error?.code === "42883" ||
    message.includes(normalisedFunctionName) &&
      (message.includes("not find") ||
        message.includes("does not exist") ||
        message.includes("schema cache"))
  );
}

async function fetchMemberTrionfiCards(creationIds) {
  const { data, error } = await supabase.rpc("get_my_trionfi_cards");

  if (!error) {
    const allowedIds = new Set(creationIds);
    return (data ?? []).filter((card) => allowedIds.has(card.creation_id));
  }

  // Temporary compatibility for deployments made before the authenticated
  // collection RPC exists. Do not hide real RPC failures.
  if (!isMissingRpc(error, "get_my_trionfi_cards")) throw error;

  const { data: fallbackData, error: fallbackError } = await supabase
    .from("Trionfi")
    .select(`
      creation_id,
      card_id,
      public_slug,
      accession_number,
      archetype_title,
      archetype_system,
      created_at,
      locale
    `)
    .in("creation_id", creationIds);

  if (fallbackError) throw fallbackError;
  return fallbackData ?? [];
}

async function fetchMemberCreationAssets(creationIds) {
  const allowedIds = new Set(creationIds);
  const { data, error } = await supabase.rpc("get_my_creation_assets");

  if (!error) {
    return (data ?? []).filter((asset) => allowedIds.has(asset.creation_id));
  }

  if (!isMissingRpc(error, "get_my_creation_assets")) {
    console.warn("Could not load creation assets through the member RPC:", error);
    return [];
  }

  // Compatibility path until the member-assets migration has been applied.
  const { data: fallbackData, error: fallbackError } = await supabase
    .from("creation_assets")
    .select("creation_id,type,engine,bucket,path,created_at")
    .in("creation_id", creationIds)
    .eq("type", "final")
    .order("created_at", { ascending: false });

  if (fallbackError) {
    console.warn("Could not load creation assets directly:", fallbackError);
    return [];
  }

  return fallbackData ?? [];
}

/**
 * Loads the creations a member is entitled to see.
 * `creation_entitlements` remains the source of truth; Trionfi is joined in a
 * second query because its case-sensitive table name makes a reverse
 * PostgREST relationship unnecessarily brittle.
 */
export async function fetchWalletStories(uid) {
  if (!uid) return [];

  const { data: rows, error } = await supabase
    .from("creation_entitlements")
    .select(`
      id,
      unlocked_at,
      creation_id,
      source,
      creations:creation_id (
        id,
        created_at,
        claimed_at,
        status,
        source,
        product_version_id,
        product_versions:product_version_id (
          id,
          slug,
          version,
          wallet_label,
          product_families:family_id ( title, type )
        )
      )
    `)
    .eq("auth_user_id", uid)
    .eq("state", "unlocked")
    .order("unlocked_at", { ascending: false });

  if (error) throw error;

  const creationIds = (rows ?? [])
    .map((row) => row.creation_id)
    .filter(Boolean);

  if (!creationIds.length) return [];

  const [savesResult, trionfiRows, creationAssetRows] = await Promise.all([
    supabase
      .from("creation_saves")
      .select("creation_id")
      .eq("auth_user_id", uid)
      .in("creation_id", creationIds),
    fetchMemberTrionfiCards(creationIds),
    fetchMemberCreationAssets(creationIds),
  ]);

  if (savesResult.error) {
    // Pins are optional presentation state; a missing save policy should not
    // prevent members from seeing creations they are entitled to access.
    console.warn("Could not load saved creations:", savesResult.error);
  }

  const savedSet = new Set(
    (savesResult.error ? [] : savesResult.data ?? []).map(
      (save) => save.creation_id
    )
  );

  const trionfiByCreation = new Map(
    trionfiRows.map((card) => [card.creation_id, card])
  );

  const assetsByCreation = new Map();

  creationAssetRows.forEach((asset) => {
    const assets = assetsByCreation.get(asset.creation_id) ?? [];
    assets.push(asset);
    assetsByCreation.set(asset.creation_id, assets);
  });

  return (rows ?? [])
    .map((row) => {
      const creation = row.creations;
      if (!creation?.id) return null;

      const productVersion = creation.product_versions;
      const family = productVersion?.product_families;
      const trionfi = trionfiByCreation.get(creation.id) ?? null;
      const familyType = family?.type || productVersion?.slug || "creation";
      const familyKey = normaliseFamilyType(familyType);
      const isTrionfi = familyKey === "trionfi" || Boolean(trionfi);
      const createdAt = trionfi?.created_at || creation.created_at;
      const isNew = isRecentlyUnlocked(row.unlocked_at);
      const generatedArtwork = isTrionfi
        ? null
        : getGeneratedArtwork(
            creation.id,
            familyKey,
            assetsByCreation.get(creation.id) ?? []
          );

      const title =
        trionfi?.archetype_title ||
        productVersion?.wallet_label ||
        family?.title ||
        productVersion?.slug ||
        "Creation";

      const image = isTrionfi
        ? resolveTrionfiCardImage(trionfi?.card_id)
        : generatedArtwork?.image || `/images/wallet/${familyType}.jpg`;

      const fallbackImage = isTrionfi
        ? TRIONFI_PLACEHOLDER
        : generatedArtwork?.fallbackImage || DEFAULT_CREATION_PLACEHOLDER;

      const route = trionfi?.public_slug
        ? `/palaisantoia/trionfi/${encodeURIComponent(trionfi.public_slug)}`
        : `/wallet/story/${creation.id}`;

      return {
        id: creation.id,
        creationId: creation.id,
        title,
        name: title,
        eyebrow: isTrionfi ? "Trionfi" : family?.title || "Antoia",
        variant:
          trionfi?.archetype_system ||
          productVersion?.wallet_label ||
          productVersion?.slug ||
          "",
        version: productVersion?.version ?? null,
        familyType,
        kind: familyType,
        createdAt,
        unlockedAt: row.unlocked_at,
        sortAt: row.unlocked_at || createdAt,
        date: formatShortDate(createdAt || row.unlocked_at),
        image,
        fallbackImage,
        imageFit: isTrionfi || Boolean(generatedArtwork) ? "contain" : "cover",
        hoverImage: isTrionfi
          ? image
          : generatedArtwork?.image || `/images/wallet/${familyType}-hover.jpg`,
        tag: isNew ? "New" : "Unlocked",
        isNew,
        saved: savedSet.has(creation.id),
        route,
        origin: isTrionfi
          ? "Created through Trionfi at Wereldmuseum Rotterdam"
          : "Created with Antoia",
        accessionNumber: trionfi?.accession_number || "",
        trionfi: trionfi
          ? {
              cardId: trionfi.card_id,
              publicSlug: trionfi.public_slug,
              accessionNumber: trionfi.accession_number,
              archetypeTitle: trionfi.archetype_title,
              archetypeSystem: trionfi.archetype_system,
              locale: trionfi.locale,
            }
          : null,
      };
    })
    .filter(Boolean);
}

export const fetchMemberCollection = fetchWalletStories;

export async function toggleSave(creationId, shouldSave, uid) {
  let userId = uid;

  if (!userId) {
    const { data: userRes, error: userErr } = await supabase.auth.getUser();
    if (userErr) throw userErr;
    userId = userRes?.user?.id;
  }

  if (!userId) throw new Error("Not signed in");
  if (!creationId) throw new Error("Missing creationId");

  if (shouldSave) {
    const { error } = await supabase
      .from("creation_saves")
      .insert([{ auth_user_id: userId, creation_id: creationId }]);

    if (error && error.code !== "23505") throw error;
    return true;
  }

  const { error } = await supabase
    .from("creation_saves")
    .delete()
    .eq("auth_user_id", userId)
    .eq("creation_id", creationId);

  if (error) throw error;
  return false;
}